import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import Loading from "@/components/Loading/Loading";
import api from "@/utils/axios";

import cls from "./LoungeClubRules.module.scss";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: 1,
    scale: [1, 1.02, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: 1,
    scale: [1.01, 1, 1.01],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const SafeImg = ({ as = "img", src, ...rest }) => {
  if (!src) return null;
  const Component = as;
  return <Component src={src} {...rest} />;
};

const LoungeClubRules = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId } = useParams();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [club, setClub] = useState(null);
  const [rules, setRules] = useState([]);

  const [idx, setIdx] = useState(0);
  const [acceptedMap, setAcceptedMap] = useState({});

  // join UI
  const [joinMode, setJoinMode] = useState(false); // ✅ when true show ONLY join UI
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState("");

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr("");
        setJoinErr("");
        setJoinMode(false);

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}/rules`);
        if (!alive) return;

        const rawRules = Array.isArray(data?.rules) ? data.rules : [];

        const sorted = [...rawRules].sort((a, b) => {
          const sa = Number.isFinite(a?.sortOrder) ? a.sortOrder : 0;
          const sb = Number.isFinite(b?.sortOrder) ? b.sortOrder : 0;
          if (sa !== sb) return sa - sb;
          const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
          const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
          return da - db;
        });

        setClub(data?.club || null);
        setRules(sorted);

        setIdx(0);
        setAcceptedMap({});
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Request failed");
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [clubId]);

  const total = rules.length;

  const current = useMemo(() => {
    if (!total) return null;
    const safe = Math.min(Math.max(idx, 0), total - 1);
    return rules[safe] || null;
  }, [rules, idx, total]);

  const clubName = club?.clubName || t("clubRules.loadingTitle");

  const isAcceptedThisRule = useMemo(() => {
    const id = current?.ruleId;
    if (!id) return false;
    return Boolean(acceptedMap[id]);
  }, [acceptedMap, current]);

  const handleGoClubSingle = () => navigate(`/lounge/clubs/single/${clubId}`);

  // backgrounds + halves from CURRENT rule
  const mobileOneSrc = current?.images?.mobileOne?.url || "";
  const mobileTwoSrc = current?.images?.mobileTwo?.url || "";
  const desktopOneSrc = current?.images?.desktopOne?.url || "";
  const desktopTwoSrc = current?.images?.desktopTwo?.url || "";

  const mLeft = current?.images?.mobileHalfLeft?.url || "";
  const mRight = current?.images?.mobileHalfRight?.url || "";
  const dLeft = current?.images?.desktopHalfLeft?.url || "";
  const dRight = current?.images?.desktopHalfRight?.url || "";

  const scrollToTop = () => window.scrollTo({ top: 200, behavior: "smooth" });

  const onNo = () => {
    scrollToTop();
    handleGoClubSingle();
  };

  const onYes = () => {
    if (!current?.ruleId) return;
    scrollToTop();
    setAcceptedMap((prev) => ({ ...prev, [current.ruleId]: true }));
  };

  const canRight = Boolean(total) && isAcceptedThisRule;

  const handleLeftHalf = () => {
    if (!total) return;

    // if in join mode, left goes back to club single (simple)
    if (joinMode) {
      handleGoClubSingle();
      return;
    }

    if (idx === 0) {
      handleGoClubSingle();
      return;
    }

    setJoinErr("");
    setIdx((v) => Math.max(0, v - 1));
  };

  const handleRightHalf = () => {
    if (!total) return;

    // if in join mode, do nothing (no rules UI)
    if (joinMode) return;

    // lock until user said YES
    if (!isAcceptedThisRule) return;

    // last rule -> switch to join mode (HIDE EVERYTHING ELSE)
    if (idx === total - 1) {
      setJoinErr("");
      setJoinMode(true);
      return;
    }

    setJoinErr("");
    setIdx((v) => Math.min(total - 1, v + 1));
  };

  const onJoinClub = async () => {
    try {
      setJoinBusy(true);
      setJoinErr("");

      await api.post(`/v1/lounge/clubs/${clubId}/join-request`, {
        message: "",
      });

      navigate(`/lounge/clubs/single/${clubId}`);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("clubRules.join.errorGeneric");
      setJoinErr(msg);
    } finally {
      setJoinBusy(false);
    }
  };

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  if (err) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <div className={cls.heroOverlayErr}>{err}</div>
        </div>
      </div>
    );
  }

  if (!current && !joinMode) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <div className={cls.heroOverlayErr}>{t("clubRules.noRules")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row (keep as is) */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA (keep as is) */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 0-.59l7.4-2.47a.5.5 0 01.63.63z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch} ${cls.clubMiniBtnActive}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 5L20.49 19l-5-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* ✅ JOIN MODE: ONLY show join request UI */}
            {joinMode ? (
              <motion.section
                className={cls.heroCardPushed}
                variants={slideInUp}
                initial="hidden"
                animate="show"
              >
                <div className={cls.rulesMetaTop}>
                  <div className={cls.rulesMetaLeft}>
                    <span className={cls.searchPromptLabel}>
                      {t("clubRules.join.title")}
                    </span>
                    <h2 className={cls.searchPromptTitle}>{clubName}</h2>
                  </div>
                </div>

                <div className={cls.joinBox}>
                  <div className={cls.joinTitle}>
                    {t("clubRules.join.title")}
                  </div>
                  <p className={cls.joinText}>{t("clubRules.join.text")}</p>

                  {joinErr ? (
                    <div className={cls.joinErr}>{joinErr}</div>
                  ) : null}

                  <div className={cls.joinButtonsRow}>
                    <button
                      type="button"
                      className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
                      onClick={handleGoClubSingle}
                      disabled={joinBusy}
                    >
                      {t("clubRules.join.cancel")}
                    </button>

                    <button
                      type="button"
                      className={`${cls.nuBtn} ${cls.nuBtnJoin}`}
                      onClick={onJoinClub}
                      disabled={joinBusy}
                    >
                      {joinBusy
                        ? t("clubRules.join.sending")
                        : t("clubRules.join.button")}
                    </button>
                  </div>
                </div>
              </motion.section>
            ) : (
              /* ✅ RULES MODE: show hero images + rule details */
              <section className={cls.searchHero}>
                <div className={cls.heroStage}>
                  {/* Mobile */}
                  <div className={cls.mobileOnly} aria-hidden="true">
                    <SafeImg
                      as={motion.img}
                      className={`${cls.heroImg} ${cls.z0}`}
                      src={mobileOneSrc}
                      alt=""
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                    />
                    <SafeImg
                      as={motion.img}
                      className={`${cls.heroImg} ${cls.z1}`}
                      src={mobileTwoSrc}
                      alt=""
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                    />

                    <button
                      type="button"
                      className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                      onClick={handleLeftHalf}
                      aria-label="Left"
                    >
                      {mLeft ? (
                        <img
                          src={mLeft}
                          alt=""
                          className={`${cls.heroHalfImg} ${cls.z3}`}
                          draggable="false"
                        />
                      ) : null}
                    </button>

                    <button
                      type="button"
                      className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                      onClick={handleRightHalf}
                      aria-label="Right"
                      disabled={!canRight}
                      data-disabled={!canRight}
                    >
                      {mRight ? (
                        <img
                          src={mRight}
                          alt=""
                          className={`${cls.heroHalfImg} ${cls.z3}`}
                          draggable="false"
                        />
                      ) : null}
                    </button>
                  </div>

                  {/* Desktop */}
                  <div className={cls.desktopOnly} aria-hidden="true">
                    <SafeImg
                      as={motion.img}
                      className={`${cls.heroImg} ${cls.z0}`}
                      src={desktopOneSrc}
                      alt=""
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                    />
                    <SafeImg
                      as={motion.img}
                      className={`${cls.heroImg} ${cls.z1}`}
                      src={desktopTwoSrc}
                      alt=""
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                    />

                    <button
                      type="button"
                      className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                      onClick={handleLeftHalf}
                      aria-label="Left"
                    >
                      {dLeft ? (
                        <img
                          src={dLeft}
                          alt=""
                          className={`${cls.heroHalfImg} ${cls.z3}`}
                          draggable="false"
                        />
                      ) : null}
                    </button>

                    <button
                      type="button"
                      className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                      onClick={handleRightHalf}
                      aria-label="Right"
                      disabled={!canRight}
                      data-disabled={!canRight}
                    >
                      {dRight ? (
                        <img
                          src={dRight}
                          alt=""
                          className={`${cls.heroHalfImg} ${cls.z3}`}
                          draggable="false"
                        />
                      ) : null}
                    </button>
                  </div>

                  <div className={cls.heroOverlayTag}>
                    {t("clubRules.progress", {
                      current: idx + 1,
                      total,
                      clubName,
                    })}
                  </div>
                </div>

                <motion.div
                  className={cls.heroCardPushed}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                >
                  <div className={cls.rulesMetaTop}>
                    <div className={cls.rulesMetaLeft}>
                      <span className={cls.searchPromptLabel}>
                        {t("clubRules.label")}
                      </span>
                      <h2 className={cls.searchPromptTitle}>
                        {current?.title || "-"}
                      </h2>
                    </div>

                    <span className={cls.ruleIndexPill}>
                      {t("clubRules.ruleXofY", { x: idx + 1, y: total })}
                    </span>
                  </div>

                  <div className={cls.rulesBody}>
                    <p className={cls.ruleLine}>
                      <span className={cls.ruleD}>
                        {current?.description || "-"}
                      </span>
                    </p>

                    {!isAcceptedThisRule ? (
                      <div className={cls.heroButtonsRow}>
                        <button
                          type="button"
                          className={`${cls.nuBtn} ${cls.nuBtnNo}`}
                          onClick={onNo}
                        >
                          {t("common.no")}
                        </button>
                        <button
                          type="button"
                          className={`${cls.nuBtn} ${cls.nuBtnYes}`}
                          onClick={onYes}
                        >
                          {t("common.yes")}
                        </button>
                      </div>
                    ) : (
                      <>
                        {current?.footer ? (
                          <div className={cls.footerBox}>
                            <p className={cls.footerText}>{current.footer}</p>
                          </div>
                        ) : null}

                        <p className={cls.hintText}>
                          {idx === total - 1
                            ? t("clubRules.hint.last")
                            : t("clubRules.hint.next")}
                        </p>
                      </>
                    )}
                  </div>
                </motion.div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoungeClubRules;
